import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  fetchDeliveries,
  markDeliveryAsActioned,
  addActionNote,
  getStatusCounts,
} from '../services/deliveryService';
import { useAuth } from './AuthContext';

const DeliveryContext = createContext(null);

export function DeliveryProvider({ children }) {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    // Total counts
    all: 0,
    actioned: 0,
    // Order type counts
    standard: 0,
    return: 0,
    exchange: 0,
    // Status counts
    pending: 0,
    failed: 0,
  });

  const lastFiltersRef = useRef({});

  const loadDeliveries = useCallback(async (filters) => {
    // If filters provided, store them; otherwise reuse last filters (e.g. after actioning)
    if (filters) {
      lastFiltersRef.current = filters;
    }
    const filtersToUse = filters || lastFiltersRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchDeliveries(filtersToUse);
      setDeliveries(data);
      const counts = await getStatusCounts();
      setStatusCounts(counts);
    } catch (err) {
      console.error('Error loading deliveries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsActioned = useCallback(async (delivery) => {
    setError(null);

    try {
      // Get all line item IDs if this is a grouped order
      const lineItemIds = delivery.lineItems
        ? delivery.lineItems.map(item => item.id)
        : [delivery.id];

      await markDeliveryAsActioned(delivery.id, user?.email, lineItemIds);
      // Reload deliveries to get fresh data
      await loadDeliveries();
    } catch (err) {
      console.error('Error marking delivery as actioned:', err);
      setError(err.message);
      throw err;
    }
  }, [user, loadDeliveries]);

  const addNote = useCallback(async (deliveryId, noteText) => {
    setError(null);

    try {
      await addActionNote(deliveryId, noteText, user?.email);
      // Reload deliveries to get fresh data
      await loadDeliveries();
    } catch (err) {
      console.error('Error adding note:', err);
      setError(err.message);
      throw err;
    }
  }, [user, loadDeliveries]);

  const value = {
    deliveries,
    loading,
    error,
    statusCounts,
    loadDeliveries,
    markAsActioned,
    addNote,
  };

  return (
    <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>
  );
}

export function useDeliveries() {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error('useDeliveries must be used within a DeliveryProvider');
  }
  return context;
}

export default DeliveryContext;
